import React, { useEffect, useState } from "react";
import { useFindMany, useFindOne, useAction, useGlobalAction } from "@gadgetinc/react";
import { api } from "../../../api";
import {
  Box,
  Heading,
  VStack,
  HStack,
  Button,
  Card,
  Text,
  Switch,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Alert,
  AlertIcon,
  Spinner,
  Divider,
  SimpleGrid,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Checkbox,
  CheckboxGroup,
  Stack,
  Radio,
  RadioGroup,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { isSystemAdmin } from "./configHelpers";
import SemesterManager from "./SemesterManager";

// Cohort Edit Modal Component
function CohortEditModal({ isOpen, onClose, cohort, onCohortUpdated, session, institutionConfig }) {
  const [{ data: updateResult, fetching: updating, error: updateError }, updateCohort] = useAction(api.cohort.update);
  const [{ fetching: repairingNullDefaults }, runNullDefaultRepair] = useGlobalAction(api.repairNullRequiredFields);

  // Fetch fresh cohort data when modal opens
  const [{ data: freshCohortData, fetching: fetchingCohort, error: cohortError }, refetchCohort] = useFindOne(api.cohort, cohort?.id, {
    select: {
      id: true,
      name: true,
      displayName: true,
      academicYear: true,
      maxStudents: true,
      startDate: true,
      endDate: true,
      isActive: true,
      shiftEligibility: true,
      enableClinicalDocNotifications: true,
      instructorPermissions: true,
      cohortAgentAccessEnabled: true,
      cohortDebriefAudioEnabled: true,
      cohortAccessibleAgents: true,
      defaultAgentAccessMode: true,
      autoGrantAgentAccess: true,
      grantScenarioAccessToInstructors: true,
      cohortScenarioGeneratorAccess: true,
      cohortHasUnlimitedScenarios: true,
      cohortScenarioGenerationLimit: true,
      cohortDocReaderEnabled: true,
      cohortEnhancedScenarioAccess: true,
      unlimitedScenarios: true,
      escalationMembers: true,
      requirements: true,
      institution: true,
      roleDefinitions: true,
      fisdapCalendarUrl: true,
      calendarSyncEnabled: true,
      calendarMode: true,
      cohortType: true,
      longitudinalTrendConfig: true,
    },
    pause: !isOpen || !cohort?.id
  });

  const [{ data: availableUsers, fetching: loadingUsers }, refetchUsers] = useFindMany(api.user, {
    filter: { institution: { equals: cohort?.institution } },
    select: {
      id: true,
      firstName: true,
      lastName: true,
      email: true,
      roles: {
        key: true
      },
      institutionRole: true,
    }
  });

  const [formData, setFormData] = useState({});
  const [attemptedCohortRepair, setAttemptedCohortRepair] = useState(false);
  const [manualRepairing, setManualRepairing] = useState(false);
  const [shiftProcessingRules, setShiftProcessingRules] = useState({
    requiresPreceptorApproval: false,
    auditNotifications: {
      notifyInstructorsOnPass: false,
      notifyInstructorsOnFail: false,
      notifyStudentsOnPass: false,
      notifyStudentsOnFail: false
    },
    notificationRecipients: {
      useCohortInstructors: false,
      customEmails: [],
      escalationRecipients: [],
      additionalRecipients: []
    },
    escalationSettings: {
      delayHours: 24,
      autoTriggers: [],
      requireApproval: false
    },
    notificationTemplates: {
      passTemplate: 'basic',
      failTemplate: 'basic',
      includeAIFeedback: false,
      includeSuggestions: false
    }
  });
  const toast = useToast();
  const cohortErrorMessage = cohortError?.message || "";
  const hasInvalidStoredData = cohortErrorMessage.includes("GGT_INVALID_STORED_DATA");
  const isCohortRepairableCorruption = hasInvalidStoredData;
  const isRepairInFlight = repairingNullDefaults || manualRepairing;

  // Clear form and fetch fresh data when modal opens
  useEffect(() => {
    if (isOpen && cohort?.id) {
      console.log('Modal opened, clearing form and refetching cohort data...');
      // Clear form data
      setFormData({});
      setAttemptedCohortRepair(false);
      setShiftProcessingRules({
        requiresPreceptorApproval: false,
        auditNotifications: {
          notifyInstructorsOnPass: false,
          notifyInstructorsOnFail: false,
          notifyStudentsOnPass: false,
          notifyStudentsOnFail: false
        },
        notificationRecipients: {
          useCohortInstructors: false,
          customEmails: [],
          escalationRecipients: [],
          additionalRecipients: []
        },
        escalationSettings: {
          delayHours: 24,
          autoTriggers: [],
          requireApproval: false
        },
        notificationTemplates: {
          passTemplate: 'basic',
          failTemplate: 'basic',
          includeAIFeedback: false,
          includeSuggestions: false
        }
      });

      // Trigger fresh data fetch
      refetchCohort();
    }
  }, [isOpen, cohort?.id]);

  useEffect(() => {
    if (
      !isOpen ||
      !cohort?.institution ||
      !isCohortRepairableCorruption ||
      attemptedCohortRepair ||
      isRepairInFlight
    ) {
      return;
    }

    setAttemptedCohortRepair(true);
    (async () => {
      try {
        await runNullDefaultRepair({
          institution: cohort.institution,
          dryRun: false,
          modelApiIdentifier: "cohort",
          errorMessage: cohortErrorMessage
        });
        await refetchCohort();
      } catch (repairError) {
        console.error("Failed to auto-repair cohort data from modal:", repairError);
      }
    })();
  }, [
    isCohortRepairableCorruption,
    cohortErrorMessage,
    isOpen,
    cohort?.institution,
    attemptedCohortRepair,
    isRepairInFlight,
    runNullDefaultRepair,
    refetchCohort
  ]);

  const handleRunRepairNow = async () => {
    if (!cohort?.institution || !isCohortRepairableCorruption || isRepairInFlight) {
      return;
    }

    setManualRepairing(true);
    setAttemptedCohortRepair(true);
    try {
      await runNullDefaultRepair({
        institution: cohort.institution,
        dryRun: false,
        modelApiIdentifier: "cohort",
        errorMessage: cohortErrorMessage
      });
      toast({
        title: "Cohort data repaired",
        description: "Repair completed. Reloading cohort...",
        status: "success",
        duration: 3500,
        isClosable: true,
      });
      await refetchCohort();
    } catch (repairError) {
      console.error("Failed to manually repair cohort data from modal:", repairError);
      toast({
        title: "Cohort repair failed",
        description: repairError?.message || "Unable to repair cohort data.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setManualRepairing(false);
    }
  };

  // Populate form with fresh cohort data
  useEffect(() => {
    if (freshCohortData) {
      console.log('Fresh cohort data received:', freshCohortData);

      setFormData({
        displayName: freshCohortData.displayName || freshCohortData.name,
        academicYear: freshCohortData.academicYear,
        maxStudents: freshCohortData.maxStudents,
        startDate: freshCohortData.startDate ? new Date(freshCohortData.startDate).toISOString().split('T')[0] : '',
        endDate: freshCohortData.endDate ? new Date(freshCohortData.endDate).toISOString().split('T')[0] : '',
        isActive: freshCohortData.isActive ?? true,
        shiftEligibility: freshCohortData.shiftEligibility || {},
        enableClinicalDocNotifications: freshCohortData.enableClinicalDocNotifications ?? false,
        escalationMembers: freshCohortData.escalationMembers || [],
        instructorPermissions: freshCohortData.instructorPermissions || {},
        cohortAgentAccessEnabled: freshCohortData.cohortAgentAccessEnabled ?? false,
        cohortDebriefAudioEnabled: freshCohortData.cohortDebriefAudioEnabled ?? true,
        cohortAccessibleAgents: freshCohortData.cohortAccessibleAgents || [],
        defaultAgentAccessMode: freshCohortData.defaultAgentAccessMode || 'none',
        autoGrantAgentAccess: freshCohortData.autoGrantAgentAccess ?? false,
        grantScenarioAccessToInstructors: freshCohortData.grantScenarioAccessToInstructors ?? false,
        cohortScenarioGeneratorAccess: freshCohortData.cohortScenarioGeneratorAccess ?? false,
        cohortHasUnlimitedScenarios: freshCohortData.cohortHasUnlimitedScenarios ?? false,
        cohortScenarioGenerationLimit: freshCohortData.cohortScenarioGenerationLimit ?? null,
        cohortDocReaderEnabled: freshCohortData.cohortDocReaderEnabled ?? false,
        cohortEnhancedScenarioAccess: freshCohortData.cohortEnhancedScenarioAccess ?? false,
        unlimitedScenarios: freshCohortData.cohortHasUnlimitedScenarios ?? freshCohortData.unlimitedScenarios ?? false,
        fisdapCalendarUrl: freshCohortData.fisdapCalendarUrl || '',
        calendarSyncEnabled: freshCohortData.calendarSyncEnabled ?? false,
        calendarMode: freshCohortData.calendarMode || 'manual',
        longitudinalTrendConfig: freshCohortData.longitudinalTrendConfig || {
          enabled: false,
          mode: "beta",
          reviewerOnly: true,
        },
      });

      // Initialize shift processing rules from fresh cohort data
      setShiftProcessingRules({
        requiresPreceptorApproval: freshCohortData.requirements?.preceptorApproval || freshCohortData.requirePreceptorApproval || false,
        enableAIEvaluation: freshCohortData.enableAIEvaluation !== false,
        notificationRecipients: freshCohortData.requirements?.notificationRecipients || {
          useCohortInstructors: false,
          customEmails: [],
          escalationRecipients: [],
          additionalRecipients: []
        },
        escalationSettings: freshCohortData.requirements?.escalationSettings || {
          delayHours: 24,
          autoTriggers: [],
          requireApproval: false
        },
        notificationTemplates: freshCohortData.requirements?.notificationTemplates || {
          passTemplate: 'basic',
          failTemplate: 'basic',
          includeAIFeedback: false,
          includeSuggestions: false
        }
      });

      console.log('Form populated with fresh data.');
    }
  }, [freshCohortData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cohort || !freshCohortData) return;

    try {
      console.log('=== COHORT UPDATE DEBUG ===');
      console.log('Original cohort data:', cohort);
      console.log('Fresh cohort data:', freshCohortData);
      console.log('Current formData:', formData);
      console.log('Current shiftProcessingRules:', shiftProcessingRules);

      const dataToUpdate = {
        displayName: formData.displayName,
        academicYear: formData.academicYear,
        maxStudents: formData.maxStudents ? parseInt(formData.maxStudents, 10) : null,
        startDate: formData.startDate ? new Date(formData.startDate + 'T00:00:00.000Z') : null,
        endDate: formData.endDate ? new Date(formData.endDate + 'T23:59:59.999Z') : null,
        isActive: formData.isActive,
        shiftEligibility: formData.shiftEligibility,
        enableClinicalDocNotifications: formData.enableClinicalDocNotifications,
        escalationMembers: formData.escalationMembers || [],
        instructorPermissions: formData.instructorPermissions || {},
        cohortAgentAccessEnabled: formData.cohortAgentAccessEnabled ?? false,
        cohortDebriefAudioEnabled: formData.cohortDebriefAudioEnabled ?? true,
        cohortAccessibleAgents: formData.cohortAccessibleAgents || [],
        defaultAgentAccessMode: formData.defaultAgentAccessMode || 'none',
        autoGrantAgentAccess: formData.autoGrantAgentAccess ?? false,
        grantScenarioAccessToInstructors: formData.grantScenarioAccessToInstructors ?? false,
        cohortScenarioGeneratorAccess: formData.cohortScenarioGeneratorAccess ?? false,
        cohortHasUnlimitedScenarios: formData.cohortHasUnlimitedScenarios ?? false,
        cohortScenarioGenerationLimit: formData.cohortHasUnlimitedScenarios
          ? null
          : (
            formData.cohortScenarioGenerationLimit === "" ||
            formData.cohortScenarioGenerationLimit === null ||
            formData.cohortScenarioGenerationLimit === undefined
              ? null
              : parseInt(formData.cohortScenarioGenerationLimit, 10)
          ),
        cohortDocReaderEnabled: formData.cohortDocReaderEnabled ?? false,
        cohortEnhancedScenarioAccess: formData.cohortEnhancedScenarioAccess ?? false,
        // Keep legacy field in sync while newer cohortHasUnlimitedScenarios is authoritative.
        unlimitedScenarios: formData.cohortHasUnlimitedScenarios ?? false,
        fisdapCalendarUrl: formData.fisdapCalendarUrl || null,
        calendarSyncEnabled: formData.calendarSyncEnabled ?? false,
        calendarMode: formData.calendarMode || 'manual',
        longitudinalTrendConfig: {
          ...(formData.longitudinalTrendConfig || {}),
          enabled: formData.longitudinalTrendConfig?.enabled === true,
          mode: "beta",
          reviewerOnly: true,
        },
        // Include roleDefinitions from the fresh cohort data (required field)
        roleDefinitions: freshCohortData.roleDefinitions,
        requirements: {
          preceptorApproval: shiftProcessingRules.requiresPreceptorApproval,
          notificationRecipients: shiftProcessingRules.notificationRecipients,
          escalationSettings: shiftProcessingRules.escalationSettings,
          notificationTemplates: shiftProcessingRules.notificationTemplates,
          enableAIEvaluation: shiftProcessingRules.enableAIEvaluation !== false
        }
      };

      console.log('Final dataToUpdate being sent:', dataToUpdate);
      console.log('=== END DEBUG ===');

      const result = await updateCohort({ id: cohort.id, ...dataToUpdate });

      console.log('Cohort update result:', result);

      toast({
        title: "Cohort Updated",
        description: "Cohort settings have been successfully updated.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onClose();

      // Multiple refresh attempts to ensure data is updated
      if (onCohortUpdated) {
        onCohortUpdated();
        setTimeout(() => onCohortUpdated(), 500);
        setTimeout(() => onCohortUpdated(), 1000);
      }
    } catch (error) {
      console.error('Cohort update error:', error);
      toast({
        title: "Error Updating Cohort",
        description: error.message || "Failed to update cohort",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleShiftEligibilityChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      shiftEligibility: {
        ...prev.shiftEligibility,
        [field]: value
      }
    }));
  };

  const handleShiftRulesChange = (field, value) => {
    console.log('Shift rules change:', field, value);
    setShiftProcessingRules(prev => {
      const newRules = { ...prev, [field]: value };
      console.log('New shift processing rules:', newRules);
      return newRules;
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white" maxH="90vh" overflowY="auto">
        <ModalHeader>Edit Cohort - {cohort?.displayName}</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(e); }}>
          <ModalBody>
            <VStack spacing={6} align="stretch">
              {updateError && (
                <Alert status="error">
                  <AlertIcon />
                  <Text>{updateError.message}</Text>
                </Alert>
              )}

              {cohortError && isCohortRepairableCorruption && isRepairInFlight && (
                <Alert status="info">
                  <AlertIcon />
                  <HStack spacing={2}>
                    <Spinner size="xs" color="blue.400" />
                    <Text>Repairing legacy cohort data, then reloading cohort…</Text>
                  </HStack>
                </Alert>
              )}

              {cohortError && isCohortRepairableCorruption && !isRepairInFlight && (
                <Alert status="warning">
                  <AlertIcon />
                  <VStack align="start" spacing={2} width="100%">
                    <Text>Failed to load cohort data: {cohortError.message}</Text>
                    <HStack spacing={3}>
                      <Text fontSize="xs" color="gray.300">
                        This looks repairable. Run repair now and retry.
                      </Text>
                      <Button
                        size="xs"
                        colorScheme="orange"
                        onClick={handleRunRepairNow}
                        isLoading={isRepairInFlight}
                        loadingText="Repairing"
                      >
                        Run Repair Now
                      </Button>
                    </HStack>
                  </VStack>
                </Alert>
              )}

              {cohortError && !isCohortRepairableCorruption && (
                <Alert status="error">
                  <AlertIcon />
                  <Text>Failed to load cohort data: {cohortError.message}</Text>
                </Alert>
              )}

              {fetchingCohort && (
                <VStack spacing={4} align="center" py={8}>
                  <Spinner size="lg" color="blue.400" />
                  <Text color="gray.300">Loading current cohort settings...</Text>
                </VStack>
              )}

              {!fetchingCohort && !freshCohortData && (
                <Alert status="warning">
                  <AlertIcon />
                  <Text>Waiting for cohort data to load...</Text>
                </Alert>
              )}

              {freshCohortData && (
                <VStack spacing={6} align="stretch">
                  {/* Basic Information */}
                  <Box>
                    <Heading size="md" mb={4}>Basic Information</Heading>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl>
                        <FormLabel>Display Name</FormLabel>
                        <Input
                          value={formData.displayName || ''}
                          onChange={(e) => handleChange('displayName', e.target.value)}
                          bg="gray.700"
                          borderColor="gray.600"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Academic Year</FormLabel>
                        <Input
                          value={formData.academicYear || ''}
                          onChange={(e) => handleChange('academicYear', e.target.value)}
                          bg="gray.700"
                          borderColor="gray.600"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Max Students</FormLabel>
                        <NumberInput>
                          <NumberInputField
                            value={formData.maxStudents || ''}
                            onChange={(e) => handleChange('maxStudents', e.target.value)}
                            bg="gray.700"
                            borderColor="gray.600"
                          />
                        </NumberInput>
                      </FormControl>
                      <FormControl>
                        <HStack>
                          <Switch
                            isChecked={formData.isActive || false}
                            onChange={(e) => handleChange('isActive', e.target.checked)}
                            colorScheme="green"
                          />
                          <FormLabel mb={0}>Active Status</FormLabel>
                        </HStack>
                      </FormControl>
                    </SimpleGrid>
                  </Box>

                  {/* Dates */}
                  <Box>
                    <Heading size="md" mb={4}>Schedule</Heading>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl>
                        <FormLabel>Start Date</FormLabel>
                        <Input
                          type="date"
                          value={formData.startDate || ''}
                          onChange={(e) => handleChange('startDate', e.target.value)}
                          bg="gray.700"
                          borderColor="gray.600"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>End Date</FormLabel>
                        <Input
                          type="date"
                          value={formData.endDate || ''}
                          onChange={(e) => handleChange('endDate', e.target.value)}
                          bg="gray.700"
                          borderColor="gray.600"
                        />
                      </FormControl>
                    </SimpleGrid>
                  </Box>

                  {/* Calendar Mode & FISDAP Sync */}
                  <Box>
                    <Heading size="md" mb={4}>Calendar & Shift Management</Heading>
                    <VStack spacing={4} align="stretch">
                      <FormControl>
                        <FormLabel>Calendar Mode</FormLabel>
                        <RadioGroup
                          value={formData.calendarMode || 'manual'}
                          onChange={(val) => {
                            handleChange('calendarMode', val);
                            if (val === 'sync') {
                              handleChange('calendarSyncEnabled', true);
                            }
                          }}
                        >
                          <Stack direction="row" spacing={6}>
                            <Radio value="manual" colorScheme="blue">
                              <Text fontSize="sm">Manual</Text>
                            </Radio>
                            <Radio value="sync" colorScheme="green">
                              <Text fontSize="sm">FISDAP Sync</Text>
                            </Radio>
                          </Stack>
                        </RadioGroup>
                        <Text fontSize="xs" color="gray.400" mt={1}>
                          {formData.calendarMode === 'sync'
                            ? "Shifts are managed by FISDAP calendar sync. Manual creation/editing is disabled."
                            : "Instructors can manually create, edit, and delete shifts."}
                        </Text>
                      </FormControl>

                      {formData.calendarMode === 'sync' && (
                        <>
                          <Alert status="info" borderRadius="md" bg="blue.900" borderColor="blue.600">
                            <AlertIcon color="blue.300" />
                            <Text fontSize="sm" color="blue.100">
                              Sync mode: shifts are read-only and managed automatically from the FISDAP calendar feed.
                            </Text>
                          </Alert>
                          <FormControl>
                            <FormLabel>FISDAP Calendar URL</FormLabel>
                            <Input
                              type="url"
                              value={formData.fisdapCalendarUrl || ''}
                              onChange={(e) => handleChange('fisdapCalendarUrl', e.target.value)}
                              placeholder="https://fisdap.net/calendar/..."
                              bg="gray.700"
                              borderColor="gray.600"
                            />
                          </FormControl>
                          <HStack justify="space-between">
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" fontWeight="medium">Calendar Sync Enabled</Text>
                              <Text fontSize="xs" color="gray.400">Automatically sync FISDAP calendar events to shifts</Text>
                            </VStack>
                            <Switch
                              isChecked={formData.calendarSyncEnabled ?? false}
                              onChange={(e) => handleChange('calendarSyncEnabled', e.target.checked)}
                              colorScheme="blue"
                            />
                          </HStack>
                          <Link to="/admin/calendar-sync" style={{ textDecoration: 'none' }}>
                            <Text fontSize="sm" color="blue.300" _hover={{ textDecoration: "underline" }}>
                              Manage Full Sync Settings →
                            </Text>
                          </Link>
                        </>
                      )}

                      {formData.calendarMode === 'manual' && (
                        <Text fontSize="xs" color="gray.500">
                          You can optionally configure a FISDAP calendar URL later by switching to Sync mode.
                        </Text>
                      )}
                    </VStack>
                  </Box>

                  {/* Semesters */}
                  <Box>
                    <Heading size="md" mb={4}>Semesters</Heading>
                    <Text fontSize="xs" color="gray.400" mb={3}>
                      Define semester date ranges for filtering shifts, compliance data, and shift-pack scoping.
                    </Text>
                    <SemesterManager
                      cohortId={cohort?.id}
                      institution={freshCohortData?.institution || cohort?.institution}
                      cohortStartDate={formData.startDate}
                      cohortEndDate={formData.endDate}
                      cohortType={freshCohortData?.cohortType}
                    />
                  </Box>

                  {/* Shift Processing & Evaluation */}
                  <Box>
                    <Heading size="md" mb={4}>Shift Processing & Evaluation</Heading>
                    <Card bg="gray.700" borderColor="gray.600" p={4}>
                      <VStack spacing={6} align="stretch">
                        {/* Basic Processing Settings */}
                        <Box>
                          <Heading size="sm" mb={3} color="gray.200">Processing Settings</Heading>
                          <VStack spacing={3} align="stretch">
                            <HStack justify="space-between">
                              <VStack align="start" spacing={0}>
                                <Text fontSize="sm" fontWeight="medium">Require Preceptor Approval</Text>
                                <Text fontSize="xs" color="gray.400">Students must get preceptor approval before enrolling in shifts</Text>
                              </VStack>
                              <Switch
                                isChecked={shiftProcessingRules.requiresPreceptorApproval}
                                onChange={(e) => handleShiftRulesChange('requiresPreceptorApproval', e.target.checked)}
                                colorScheme="blue"
                              />
                            </HStack>

                            <HStack justify="space-between">
                              <VStack align="start" spacing={0}>
                                <Text fontSize="sm" fontWeight="medium">AI Evaluation Enabled</Text>
                                <Text fontSize="xs" color="gray.400">Enable automated AI evaluation of shift documentation</Text>
                              </VStack>
                              <Switch
                                isChecked={shiftProcessingRules.enableAIEvaluation !== false}
                                onChange={(e) => handleShiftRulesChange('enableAIEvaluation', e.target.checked)}
                                colorScheme="purple"
                              />
                            </HStack>
                          </VStack>
                        </Box>

                        <Divider borderColor="gray.600" />

                        {/* Escalation Settings */}
                        <Box>
                          <Heading size="sm" mb={3} color="gray.200">Escalation Settings</Heading>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <FormControl>
                              <FormLabel fontSize="sm">Escalation Delay (Hours)</FormLabel>
                              <NumberInput
                                value={shiftProcessingRules.escalationSettings?.delayHours || 24}
                                onChange={(value) => handleShiftRulesChange('escalationSettings', {
                                  ...shiftProcessingRules.escalationSettings,
                                  delayHours: parseInt(value) || 24
                                })}
                                min={1}
                                max={168}
                                size="sm"
                              >
                                <NumberInputField bg="gray.600" borderColor="gray.500" />
                              </NumberInput>
                              <Text fontSize="xs" color="gray.400" mt={1}>
                                Hours before failed evaluations are escalated
                              </Text>
                            </FormControl>

                            <FormControl>
                              <HStack justify="space-between">
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="sm" fontWeight="medium">Auto-Escalation</Text>
                                  <Text fontSize="xs" color="gray.400">Automatically escalate failed evaluations</Text>
                                </VStack>
                                <Switch
                                  isChecked={shiftProcessingRules.escalationSettings?.enableAutoEscalation || false}
                                  onChange={(e) => handleShiftRulesChange('escalationSettings', {
                                    ...shiftProcessingRules.escalationSettings,
                                    enableAutoEscalation: e.target.checked
                                  })}
                                  colorScheme="orange"
                                  size="sm"
                                />
                              </HStack>
                            </FormControl>
                          </SimpleGrid>
                        </Box>
                      </VStack>
                    </Card>
                  </Box>

                  {/* Permissions & Access */}
                  <Box>
                    <Heading size="md" mb={4}>Permissions & Access</Heading>
                    <Card bg="gray.700" borderColor="gray.600" p={4}>
                      <VStack spacing={4} align="stretch">
                        <Box>
                          <Heading size="sm" mb={3} color="gray.200">Shift Eligibility</Heading>
                          <Text fontSize="sm" color="gray.400" mb={3}>
                            Select which types of shifts this cohort can enroll in
                          </Text>
                          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                            <HStack>
                              <Switch
                                isChecked={formData.shiftEligibility?.teamLeadEligible || false}
                                onChange={(e) => handleShiftEligibilityChange('teamLeadEligible', e.target.checked)}
                                colorScheme="purple"
                                size="sm"
                              />
                              <Text fontSize="sm">Team Lead</Text>
                            </HStack>
                            <HStack>
                              <Switch
                                isChecked={formData.shiftEligibility?.instructorEligible || false}
                                onChange={(e) => handleShiftEligibilityChange('instructorEligible', e.target.checked)}
                                colorScheme="green"
                                size="sm"
                              />
                              <Text fontSize="sm">Instructor</Text>
                            </HStack>
                            <HStack>
                              <Switch
                                isChecked={formData.shiftEligibility?.preceptorEligible || false}
                                onChange={(e) => handleShiftEligibilityChange('preceptorEligible', e.target.checked)}
                                colorScheme="orange"
                                size="sm"
                              />
                              <Text fontSize="sm">Preceptor</Text>
                            </HStack>
                            <HStack>
                              <Switch
                                isChecked={formData.shiftEligibility?.fieldInternshipEligible || false}
                                onChange={(e) => handleShiftEligibilityChange('fieldInternshipEligible', e.target.checked)}
                                colorScheme="cyan"
                                size="sm"
                              />
                              <Text fontSize="sm">Field Internship</Text>
                            </HStack>
                          </SimpleGrid>
                        </Box>
                      </VStack>
                    </Card>
                  </Box>



                  {/* Cohort Agent Access Settings */}
                  <Box>
                    <Heading size="md" mb={4}>Cohort Agent Access Settings</Heading>
                    <Card bg="gray.700" borderColor="gray.600" p={4}>
                      <VStack spacing={6} align="stretch">
                        <Box>
                          <HStack justify="space-between">
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" fontWeight="medium">Enable Cohort-Managed Agent Access</Text>
                              <Text fontSize="xs" color="gray.400">Allow this cohort to grant agent access to its members</Text>
                            </VStack>
                            <Switch
                              isChecked={formData.cohortAgentAccessEnabled || false}
                              onChange={(e) => handleChange('cohortAgentAccessEnabled', e.target.checked)}
                              colorScheme="purple"
                            />
                          </HStack>
                        </Box>

                        {formData.cohortAgentAccessEnabled && (
                          <VStack spacing={4} align="stretch">
                            <Box>
                              <FormLabel fontSize="sm">Available Agents for Cohort</FormLabel>
                              <Text fontSize="xs" color="gray.400" mb={3}>
                                Select which agents this cohort can grant access to for its members
                              </Text>
                              <CheckboxGroup
                                value={formData.cohortAccessibleAgents || []}
                                onChange={(values) => {
                                  handleChange('cohortAccessibleAgents', values);
                                  // Auto-set defaultAgentAccessMode based on selection
                                  if (values.length === 0) {
                                    handleChange('defaultAgentAccessMode', 'none');
                                  } else if (values.length === 4) {
                                    handleChange('defaultAgentAccessMode', 'all');
                                  } else {
                                    handleChange('defaultAgentAccessMode', 'specific');
                                  }
                                }}
                              >
                                <Stack spacing={2}>
                                  <Checkbox value="don" colorScheme="purple">
                                    <Text fontSize="sm">Don (General Assistant)</Text>
                                  </Checkbox>
                                  <Checkbox value="maia" colorScheme="purple">
                                    <Text fontSize="sm">Maia (Clinical AI)</Text>
                                  </Checkbox>
                                  <Checkbox value="gerald" colorScheme="purple">
                                    <Text fontSize="sm">Gerald (Documentation)</Text>
                                  </Checkbox>
                                  <Checkbox value="glenn" colorScheme="purple">
                                    <Text fontSize="sm">Glenn (Voice Agent)</Text>
                                  </Checkbox>
                                </Stack>
                              </CheckboxGroup>
                              <Text fontSize="xs" color="gray.400" mt={3} fontStyle="italic">
                                {(() => {
                                  const selectedCount = (formData.cohortAccessibleAgents || []).length;
                                  if (selectedCount === 0) {
                                    return "No agents selected - members will have no access";
                                  } else if (selectedCount === 4) {
                                    return "All agents selected - members will have full agent access";
                                  } else {
                                    return "Some agents selected - members will have specific agent access";
                                  }
                                })()}
                              </Text>
                            </Box>

                            <Box>
                              <HStack justify="space-between">
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="sm" fontWeight="medium">Auto-grant to New Members</Text>
                                  <Text fontSize="xs" color="gray.400">Automatically grant cohort agent access to new members</Text>
                                </VStack>
                                <Switch
                                  isChecked={formData.autoGrantAgentAccess || false}
                                  onChange={(e) => handleChange('autoGrantAgentAccess', e.target.checked)}
                                  colorScheme="green"
                                />
                              </HStack>
                            </Box>

                            <Box>
                              <HStack justify="space-between">
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="sm" fontWeight="medium">Enable Scenario Generator for This Cohort</Text>
                                  <Text fontSize="xs" color="gray.400">
                                    Master switch for cohort-based scenario generator access.
                                  </Text>
                                </VStack>
                                <Switch
                                  isChecked={formData.cohortScenarioGeneratorAccess || false}
                                  onChange={(e) => handleChange('cohortScenarioGeneratorAccess', e.target.checked)}
                                  colorScheme="blue"
                                />
                              </HStack>
                            </Box>

                            <Box>
                              <HStack justify="space-between">
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="sm" fontWeight="medium">Grant Scenario Generator Access to All Instructors in this Cohort</Text>
                                  <Text fontSize="xs" color="gray.400">When enabled, all instructors in this cohort will automatically have access to the scenario generator</Text>
                                </VStack>
                                <Switch
                                  isChecked={formData.grantScenarioAccessToInstructors || false}
                                  onChange={(e) => handleChange('grantScenarioAccessToInstructors', e.target.checked)}
                                  colorScheme="purple"
                                />
                              </HStack>
                            </Box>

                            <Box>
                              <HStack justify="space-between">
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="sm" fontWeight="medium">Enable Clinical Reader for Students in this Cohort</Text>
                                  <Text fontSize="xs" color="gray.400">
                                    Allows student cohort members to use the clinical document reader.
                                  </Text>
                                </VStack>
                                <Switch
                                  isChecked={formData.cohortDocReaderEnabled || false}
                                  onChange={(e) => handleChange('cohortDocReaderEnabled', e.target.checked)}
                                  colorScheme="teal"
                                />
                              </HStack>
                            </Box>

                            <Box>
                              <HStack justify="space-between">
                                <VStack align="start" spacing={0}>
                                  <HStack spacing={2}>
                                    <Text fontSize="sm" fontWeight="medium">Longitudinal Trends</Text>
                                    <Badge colorScheme="purple">Beta</Badge>
                                  </HStack>
                                  <Text fontSize="xs" color="gray.400">
                                    Reviewer-only compact trend profiles for FISDAP shift evaluations.
                                  </Text>
                                </VStack>
                                <Switch
                                  isChecked={formData.longitudinalTrendConfig?.enabled === true}
                                  onChange={(e) => handleChange('longitudinalTrendConfig', {
                                    ...(formData.longitudinalTrendConfig || {}),
                                    enabled: e.target.checked,
                                    mode: "beta",
                                    reviewerOnly: true,
                                  })}
                                  colorScheme="purple"
                                />
                              </HStack>
                            </Box>

                            <Box>
                              <HStack justify="space-between">
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="sm" fontWeight="medium">Enable Live Debrief Audio for This Cohort</Text>
                                  <Text fontSize="xs" color="gray.400">
                                    Controls live evaluated scenario audio capture/transcription for this cohort only. Voice-sim debrief is unaffected.
                                  </Text>
                                </VStack>
                                <Switch
                                  isChecked={formData.cohortDebriefAudioEnabled ?? true}
                                  onChange={(e) => handleChange('cohortDebriefAudioEnabled', e.target.checked)}
                                  colorScheme="orange"
                                />
                              </HStack>
                            </Box>

                            <Box>
                              <HStack justify="space-between">
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="sm" fontWeight="medium">Unlimited Scenarios for Cohort Members</Text>
                                  <Text fontSize="xs" color="gray.400">When enabled, all active members of this cohort will have unlimited scenario generation</Text>
                                </VStack>
                                <Switch
                                  isChecked={formData.cohortHasUnlimitedScenarios || false}
                                  onChange={(e) => {
                                    handleChange('cohortHasUnlimitedScenarios', e.target.checked);
                                    // Keep legacy flag synchronized.
                                    handleChange('unlimitedScenarios', e.target.checked);
                                  }}
                                  colorScheme="green"
                                />
                              </HStack>
                            </Box>

                            {!formData.cohortHasUnlimitedScenarios && (
                              <Box>
                                <FormLabel fontSize="sm">Cohort Scenario Generation Limit</FormLabel>
                                <NumberInput min={0} value={formData.cohortScenarioGenerationLimit ?? ""}>
                                  <NumberInputField
                                    bg="gray.700"
                                    borderColor="gray.600"
                                    onChange={(e) => handleChange("cohortScenarioGenerationLimit", e.target.value)}
                                    placeholder="0 (blocked)"
                                  />
                                </NumberInput>
                                <Text fontSize="xs" color="gray.400" mt={2}>
                                  Maximum scenarios allowed for this cohort each year when unlimited access is disabled.
                                </Text>
                              </Box>
                            )}

                            {/* Enhanced Scenario Access - Only show if institution has it enabled */}
                            {institutionConfig?.enableEnhancedScenarios === true && (
                              <Box
                                p={4}
                                bg="purple.900"
                                bgOpacity="0.2"
                                border="2px solid"
                                borderColor="purple.500"
                                borderRadius="md"
                              >
                                <HStack justify="space-between" align="center">
                                  <VStack align="start" spacing={1}>
                                    <HStack>
                                      <Text fontSize="sm" fontWeight="bold" color="purple.300">⚡ Enhanced Scenario Generation</Text>
                                      <Badge colorScheme="purple" variant="solid" fontSize="xs">Premium</Badge>
                                    </HStack>
                                    <Text fontSize="xs" color="gray.400">
                                      Grant access to GPT-5 with advanced verbosity controls and enhanced generation capabilities
                                    </Text>
                                  </VStack>
                                  <Switch
                                    isChecked={formData.cohortEnhancedScenarioAccess === true}
                                    onChange={(e) => handleChange('cohortEnhancedScenarioAccess', e.target.checked)}
                                    colorScheme="purple"
                                    size="lg"
                                  />
                                </HStack>
                                {formData.cohortEnhancedScenarioAccess === true && (
                                  <Text fontSize="xs" color="purple.300" mt={3} fontWeight="medium">
                                    ⚡ All active members of this cohort will have access to enhanced scenario generation features
                                  </Text>
                                )}
                              </Box>
                            )}
                          </VStack>
                        )}
                      </VStack>
                    </Card>
                  </Box>
                </VStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={updating || fetchingCohort}
                loadingText={fetchingCohort ? "Loading..." : "Updating..."}
                isDisabled={fetchingCohort || !freshCohortData}
              >
                Update Cohort
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

export default CohortEditModal;
